import React, { useState, useEffect } from "react";
import { useDebounce } from '../../hooks/usedeBounce';
import { useNavigate } from "react-router-dom";
import {
  Table,
  InputGroup,
  FormControl,
  Pagination,
  Form,
  Toast,
} from "react-bootstrap";

import { FaRegCopy, FaExternalLinkAlt } from "react-icons/fa";
import { IoAddCircle, IoRemoveCircleOutline } from "react-icons/io5";

import Header from "../../components/Header";
import NavBar from "../../components/NavBar";
import Footer from "../../components/Footer";
import styles from "./Grilla.module.css";
import { Funcionario } from "../../types/interfaces";
import { useLoading } from "../../components/LoadingContext";
import { useAppContext } from "../../components/AppContext";
import ResizableTh from "../Grilla/ResizableTable";
import Favorite from "../Grilla/Favorite"

const Grilla: React.FC = () => {
  const { setLoading } = useLoading();
  const navigate = useNavigate();
  const [funcionarios, setFuncionarios] = useState<Funcionario[]>([]);
  const [globalFilterValue, setGlobalFilterValue] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(25);
  const [sortConfig, setSortConfig] = useState<{
    key: string | null;
    direction: string | null;
  }>({ key: 'funcionario', direction: 'ASC' });
  const [totalItems, setTotalItems] = useState<number>(0);
  const [expandedRows, setExpandedRows] = useState<number[]>([]);
  const [inputValue, setInputValue] = useState('');
  const debouncedFilter = useDebounce(inputValue, 1500);
  const { user, isSessionReady } = useAppContext();
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

useEffect(() => {
  if (!user || !isSessionReady) return;

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/agenda/funcionarios?page=${currentPage}&pageSize=${itemsPerPage}&sortBy=${sortConfig.key}&sortOrder=${sortConfig.direction}&filter=${globalFilterValue}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        }
      );
      if (!response.ok) {
        throw new Error("Error al cargar los datos");
      }
      const data = await response.json();
      setFuncionarios(data.data);
      setTotalItems(data.total);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  fetchData();
}, [user?.email, isSessionReady, currentPage, itemsPerPage, sortConfig, globalFilterValue]);

  const handleToggleFavorite = (id: number) => {
    setFuncionarios(prev =>
      prev.map(f => f.id === id ? { ...f, es_favorito: !f.es_favorito } : f)
    );
  };

  const handleNotify = (message: string, type: 'success' | 'error' = 'success') => {
    setToastMessage(message);
    setShowToast(true);
  };

  const onFuncionarioClick = async (id: number) => {
    navigate(`/organigrama?funcionarioId=${id}`);
  };

  const onOrganicaClick = async (id: number) => {
    navigate(`/organigrama?organicaId=${id}`);
  };

  const onEstablecimientoClick = async (id: number) => {
    navigate(`/establecimientos?establecimientoId=${id}`);
  };

  useEffect(() => {
    const normalized = debouncedFilter
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');

    setGlobalFilterValue(normalized);
    setCurrentPage(1);
  }, [debouncedFilter]);

  const onGlobalFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const onSort = (key: string) => {
    let direction = "ASC";
    if (
      sortConfig &&
      sortConfig.key === key &&
      sortConfig.direction === "ASC"
    ) {
      direction = "DESC";
    }
    setSortConfig({ key, direction });
  };

  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const changeItemsPerPage = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setItemsPerPage(parseInt(e.target.value, 10));
    setCurrentPage(1);
  };

  const registersControls = (
    <div
      className={`d-flex justify-content-between align-items-center mt-3" ${styles.registersControls}`}
    >
      <div className={styles.registerControlsText}>Mostrar</div>
      <Form.Select
        value={itemsPerPage}
        onChange={changeItemsPerPage}
        className="w-auto"
      >
        <option value={25}>25</option>
        <option value={50}>50</option>
        <option value={100}>100</option>
      </Form.Select>
      <div className={styles.registerControlsText}>registros.</div>
    </div>
  );

  const paginationControls = (
    <div className={styles.paginationControls}>
      <Pagination>
        <Pagination.First
          onClick={() => paginate(1)}
          disabled={currentPage === 1}
        />
        <Pagination.Prev
          onClick={() => paginate(currentPage - 1)}
          disabled={currentPage === 1}
        />
        <Pagination.Item
          disabled
        >{`Página ${currentPage} de ${totalPages}`}</Pagination.Item>
        <Pagination.Next
          onClick={() => paginate(currentPage + 1)}
          disabled={currentPage === totalPages}
        />
        <Pagination.Last
          onClick={() => paginate(totalPages)}
          disabled={currentPage === totalPages}
        />
      </Pagination>
    </div>
  );

  const renderHeader = () => (
    <div className="d-flex justify-content-between align-items-center">
      <InputGroup className={styles.searchBarWidth}>
        <InputGroup.Text>
          <i className="pi pi-search" />
        </InputGroup.Text>
        <FormControl
          placeholder="Buscar"
          value={inputValue}
          onChange={onGlobalFilterChange}
        />
      </InputGroup>
      {registersControls}
    </div>
  );

  const renderSortIcon = (key: string) => {
    if (!sortConfig || sortConfig.key !== key) {
      return (
        <div>
          <span className={styles.sortIconDisabled}>▲</span>
          <span className={styles.sortIconDisabled}>▼</span>
        </div>
      );
    }
    return sortConfig.direction === "ASC" ? "▲" : "▼";
  };

  const getColSpan = () => {
    const width = window.innerWidth;
    if (width < 576) return 1;
    if (width < 768) return 2;
    if (width < 992) return 4;
    if (width < 1200) return 7;
    return 10;
  };

  const renderTableBody = () => {
    if (funcionarios.length === 0) {
      return (
        <tr>
          <td colSpan={getColSpan()} className="text-center">
          No se encontraron resultados para "{globalFilterValue}"
          </td>
        </tr>
      );
    }
  };

  const toggleRowExpansion = (id: number) => {
    if (window.innerWidth < 576) {
      setExpandedRows((prevState) =>
        prevState.includes(id)
          ? prevState.filter((rowId) => rowId !== id)
          : [...prevState, id]
      );
    } else if (window.innerWidth < 1200) {
      setExpandedRows((prevState) =>
        prevState.includes(id)
          ? prevState.filter((rowId) => rowId !== id)
          : [...prevState, id]
      );
    }
  };

  return (
    <>
      <Header />
      <NavBar />
      <div className={styles.tableContainer}>
        <h2 className={styles.titlesRoutes}>Agenda</h2>
        {renderHeader()}
        <div className={styles.tableWrapper}>
          <Table striped bordered hover responsive className={styles.table}>
            <thead className={`${styles.headerCell}`}>
              <tr className={`${styles.headerCell}`}>
                <ResizableTh className={styles.headerCell}>
                  <div className={styles.headerContent} onClick={() => onSort("funcionario")}>
                    <span className={styles.headerText}>Funcionario/a</span>
                    {renderSortIcon("funcionario")}
                  </div>
                </ResizableTh>
                <ResizableTh className={`d-none d-sm-table-cell ${styles.headerCell}`}>
                  <div className={styles.headerContent} onClick={() => onSort("organica.nombre")}>
                    <span className={styles.headerText}>Orgánica</span>
                    {renderSortIcon("organica.nombre")}
                  </div>
                </ResizableTh>
                <ResizableTh className={`d-none d-md-table-cell ${styles.headerCell}`}>
                  <div className={styles.headerContent} onClick={() => onSort("establecimiento.nombre")}>
                    <span className={styles.headerText}>Establecimiento</span>
                    {renderSortIcon("establecimiento.nombre")}
                  </div>
                </ResizableTh>
                <ResizableTh className={`d-none d-md-table-cell ${styles.headerCell}`}>
                  <div className={styles.headerContent} onClick={() => onSort("ubi_adicional")}>
                    <span className={styles.headerText}>Ubi Adicional</span>
                    {renderSortIcon("ubi_adicional")}
                  </div>
                </ResizableTh>
                <ResizableTh className={`d-none d-lg-table-cell ${styles.headerInternoCell}`}>
                  <div className={styles.headerContent} onClick={() => onSort("interno")}>
                    <span className={styles.headerText}>Interno</span>
                    {renderSortIcon("interno")}
                  </div>
                </ResizableTh>
                <ResizableTh className={`d-none d-lg-table-cell ${styles.headerCell}`}>
                  <div className={styles.headerContent} onClick={() => onSort("correo")}>
                    <span className={styles.headerText}>Correo electrónico</span>
                    {renderSortIcon("correo")}
                  </div>
                </ResizableTh>
                <ResizableTh className={`d-none d-lg-table-cell ${styles.headerCell}`}>
                  <div className={styles.headerContent} onClick={() => onSort("usuario_gde")}>
                    <span className={styles.headerText}>Usuario GDE</span>
                    {renderSortIcon("usuario_gde")}
                  </div>
                </ResizableTh>
                <ResizableTh className={`d-none d-xl-table-cell ${styles.headerCell}`}>
                  <div className={styles.headerContent} onClick={() => onSort("reparticion_gde.nombre")}>
                    <span className={styles.headerText}>Reparticion GDE</span>
                    {renderSortIcon("reparticion_gde.nombre")}
                  </div>
                </ResizableTh>
                <ResizableTh className={`d-none d-xl-table-cell ${styles.headerCell}`}>
                  <div className={styles.headerContent} onClick={() => onSort("sector_gde.nombre")}>
                    <span className={styles.headerText}>Sector GDE</span>
                    {renderSortIcon("sector_gde.nombre")}
                  </div>
                </ResizableTh>
                <ResizableTh className={`d-none d-xl-table-cell ${styles.headerCell}`}>
                  <div className={styles.headerContent} onClick={() => onSort("revista")}>
                    <span className={styles.headerText}>Revista</span>
                    {renderSortIcon("revista")}
                  </div>
                </ResizableTh>
              </tr>
            </thead>
            <tbody>
              {funcionarios.map((funcionario) => (
                <React.Fragment key={funcionario.id}>
                  <tr
                    className={
                      expandedRows.includes(funcionario.id) ? "expanded" : ""
                    }
                  >
                    <td title={funcionario.funcionario}>
                      <span
                        className={`d-xl-none ${styles.expandIcon}`}
                        onClick={() => toggleRowExpansion(funcionario.id)}
                      >
                        {expandedRows.includes(funcionario.id) ? <IoRemoveCircleOutline /> : <IoAddCircle />}
                      </span>
                        <Favorite
                          id={funcionario.id}
                          isFavorite={funcionario.es_favorito}
                          onToggle={handleToggleFavorite}
                          onNotify={handleNotify}
                        />
                      <span
                        className={styles.clickeableColumns}
                        onClick={() => onFuncionarioClick(funcionario.id)}
                      >
                        {funcionario.funcionario || <div className="d-flex justify-content-center"><em className="text-muted">—</em></div>}
                      </span>
                    </td>
                    <td
                      title={funcionario.organica.nombre}
                      className="d-none d-sm-table-cell"
                    >
                      <span
                        className={styles.clickeableColumns}
                        onClick={() => onOrganicaClick(funcionario.organica_id)}
                      >
                        {funcionario.organica.nombre || <div className="d-flex justify-content-center"><em className="text-muted">—</em></div>}
                      </span>
                    </td>
                    <td
                      title={funcionario.establecimiento?.nombre}
                      className={`d-none d-md-table-cell`}
                    >
                      <span
                      className={styles.clickeableColumns}
                        onClick={() => onEstablecimientoClick(funcionario.establecimiento_id)}
                      >
                        {funcionario.establecimiento?.nombre || <div className="d-flex justify-content-center"><em className="text-muted">—</em></div>}
                      </span>
                    </td>
                    <td
                      title={funcionario.ubi_adicional}
                      className={`d-none d-md-table-cell`}
                    >
                      {funcionario.ubi_adicional || <div className="d-flex justify-content-center"><em className="text-muted">—</em></div>}
                    </td>
                    <td
                      title={funcionario.interno}
                      className={`d-none d-lg-table-cell`}
                    >
                      {funcionario.interno || <div className="d-flex justify-content-center"><em className="text-muted">—</em></div>}
                    </td>
                    <td
                      title={`Enviar correo`}
                      className={`d-none d-lg-table-cell`}
                    >
                      <a
                        href={`https://outlook.office.com/owa/?path=/mail/action/compose&to=${encodeURIComponent(
                          funcionario.correo
                        )}`}
                        onClick={(e) => {
                          e.preventDefault();
                          const target = e.target as HTMLAnchorElement;
                          const href = target.getAttribute("href");
                          if (href) {
                            window.open(
                              href,
                              "_blank",
                              "toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=yes, resizable=yes, width=600, height=400"
                            );
                          }
                        }}
                      >
                        {funcionario.correo || <div className="d-flex justify-content-center"><em className="text-muted">Sin datos</em></div>}
                      </a>
                    </td>
                    <td
                      title={funcionario.usuario_gde}
                      className={`d-none d-lg-table-cell`}
                    >
                      <div className={styles.containerColumnsGde}>
                        <div className={styles.columnsGde}>
                          {funcionario.usuario_gde || <div className="d-flex justify-content-center"><em className="text-muted">Sin datos</em></div>}
                        </div>
                        <div className={styles.actionsGde}>
                          <button
                            title={`Copiar al portapapeles`}
                            type="button"
                            onClick={() => {
                              navigator.clipboard.writeText(
                                funcionario.usuario_gde
                              );
                            }}
                            className={styles.copyButton}
                            aria-label="Copiar"
                          >
                            <FaRegCopy />
                          </button>
                          <a
                            title={`Ir a GDE`}
                            href={`https://portal.gde.gob.ar/`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <FaExternalLinkAlt />
                          </a>
                        </div>
                      </div>
                    </td>
                    <td
                      title={funcionario.reparticion_gde?.nombre}
                      className={`d-none d-xl-table-cell`}
                    >
                      <div className={styles.containerColumnsGde}>
                        <div className={styles.columnsGde}>
                          {funcionario.reparticion_gde?.nombre || <div className="d-flex justify-content-center"><em className="text-muted">Sin datos</em></div>}
                        </div>
                        <div className={styles.actionsGde}>
                          <button
                            title={`Copiar al portapapeles`}
                            type="button"
                            onClick={() => {
                              navigator.clipboard.writeText(
                                funcionario.reparticion_gde?.nombre
                              );
                            }}
                            className={styles.copyButton}
                            aria-label="Copiar"
                          >
                            <FaRegCopy />
                          </button>
                          <a
                            title={`Ir a GDE`}
                            href={`https://portal.gde.gob.ar/`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <FaExternalLinkAlt />
                          </a>
                        </div>
                      </div>
                    </td>
                    <td
                      title={funcionario.sector_gde?.nombre}
                      className="d-none d-xl-table-cell"
                    >
                      <div className={styles.containerColumnsGde}>
                        <div className={styles.columnsGde}>
                          {funcionario.sector_gde?.nombre || <div className="d-flex justify-content-center"><em className="text-muted">Sin datos</em></div>}
                        </div>
                        <div className={styles.actionsGde}>
                          <button
                            title="Copiar al portapapeles"
                            type="button"
                            onClick={() => {
                              navigator.clipboard.writeText(
                                funcionario.sector_gde?.nombre
                              );
                            }}
                            className={styles.copyButton}
                            aria-label="Copiar"
                          >
                            <FaRegCopy />
                          </button>
                          <a
                            title="Ir a GDE"
                            href="https://portal.gde.gob.ar/"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <FaExternalLinkAlt />
                          </a>
                        </div>
                      </div>
                    </td>
                    <td
                      title={funcionario.revista}
                      className={`d-none d-xl-table-cell`}
                    >
                      {funcionario.revista || <div className="d-flex justify-content-center"><em className="text-muted">—</em></div>}
                    </td>
                  </tr>
                  {expandedRows.includes(funcionario.id) && (
                    <tr className="expand">
                      <td colSpan={getColSpan()}>
                        {window.innerWidth < 576 && (
                          <div>
                            <div className={styles.expandedText}>
                              <strong>Orgánica:</strong>{" "}
                              <span
                                className={styles.clickeableColumns}
                                onClick={() =>
                                  onOrganicaClick(funcionario.organica_id)
                                }
                              >
                                {funcionario.organica.nombre}
                              </span>
                            </div>
                            <div className={styles.expandedText}>
                              <strong>Establecimiento:</strong>{" "}
                              <span
                                className={styles.clickeableColumns}
                                onClick={() =>
                                  onEstablecimientoClick(funcionario.establecimiento_id)
                                  }
                                >
                              {funcionario.establecimiento?.nombre}
                              </span>
                            </div>
                            <div className={styles.expandedText}>
                              <strong>Ubi Adicional:</strong>{" "}
                              {funcionario.ubi_adicional}
                            </div>
                            <div className={styles.expandedText}>
                              <strong>Interno:</strong> {funcionario.interno}
                            </div>
                            <div className={styles.expandedText}>
                              <strong>Correo:</strong>{" "}
                              <a
                                href={`https://outlook.office.com/owa/?path=/mail/action/compose&to=${encodeURIComponent(
                                  funcionario.correo
                                )}`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  const target = e.target as HTMLAnchorElement;
                                  const href = target.getAttribute("href");
                                  if (href) {
                                    window.open(
                                      href,
                                      "_blank",
                                      "toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=yes, resizable=yes, width=600, height=400"
                                    );
                                  }
                                }}
                              >
                                {funcionario.correo}
                              </a>
                            </div>
                            <div className={styles.expandedText}>
                              <strong>Usuario GDE:</strong>{" "}
                              {funcionario.usuario_gde}
                              <button
                                title={`Copiar al portapapeles`}
                                type="button"
                                onClick={() => {
                                  navigator.clipboard.writeText(
                                    funcionario.usuario_gde
                                  );
                                }}
                                className={styles.copyButton}
                                aria-label="Copiar"
                              >
                                <FaRegCopy />
                              </button>
                              <a
                                title={`Ir a GDE`}
                                href={`https://portal.gde.gob.ar/`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <FaExternalLinkAlt />
                              </a>
                            </div>
                            <div className={styles.expandedText}>
                              <strong>Reparticion GDE:</strong>{" "}
                              {funcionario.reparticion_gde.nombre}
                              <button
                                title={`Copiar al portapapeles`}
                                type="button"
                                onClick={() => {
                                  navigator.clipboard.writeText(
                                    funcionario.reparticion_gde.nombre
                                  );
                                }}
                                className={styles.copyButton}
                                aria-label="Copiar"
                              >
                                <FaRegCopy />
                              </button>
                              <a
                                title={`Ir a GDE`}
                                href={`https://portal.gde.gob.ar/`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <FaExternalLinkAlt />
                              </a>
                            </div>
                            <div className={styles.expandedText}>
                              <strong>Sector GDE:</strong>{" "}
                              {funcionario.sector_gde.nombre}
                              <button
                                title={`Copiar al portapapeles`}
                                type="button"
                                onClick={() => {
                                  navigator.clipboard.writeText(
                                    funcionario.sector_gde.nombre
                                  );
                                }}
                                className={styles.copyButton}
                                aria-label="Copiar"
                              >
                                <FaRegCopy />
                              </button>
                              <a
                                title={`Ir a GDE`}
                                href={`https://portal.gde.gob.ar/`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <FaExternalLinkAlt />
                              </a>
                            </div>
                            <div className={styles.expandedText}>
                              <strong>Revista:</strong> {funcionario.revista}
                            </div>
                          </div>
                        )}
                        {window.innerWidth >= 576 &&
                          window.innerWidth < 768 && (
                            <div>
                              <div className={styles.expandedText}>
                                <strong>Establecimiento:</strong>{" "}
                                <span
                                className={styles.clickeableColumns}
                                onClick={() =>
                                  onEstablecimientoClick(funcionario.establecimiento_id)
                                  }
                                >
                              {funcionario.establecimiento?.nombre}
                              </span>
                              </div>
                              <div className={styles.expandedText}>
                                <strong>Ubi Adicional:</strong>{" "}
                                {funcionario.ubi_adicional}
                              </div>
                              <div className={styles.expandedText}>
                                <strong>Interno:</strong> {funcionario.interno}
                              </div>
                              <div className={styles.expandedText}>
                                <strong>Correo electrónico:</strong>{" "}
                                <a
                                  href={`https://outlook.office.com/owa/?path=/mail/action/compose&to=${encodeURIComponent(
                                    funcionario.correo
                                  )}`}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    const target =
                                      e.target as HTMLAnchorElement;
                                    const href = target.getAttribute("href");
                                    if (href) {
                                      window.open(
                                        href,
                                        "_blank",
                                        "toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=yes, resizable=yes, width=600, height=400"
                                      );
                                    }
                                  }}
                                >
                                  {funcionario.correo}
                                </a>
                              </div>
                              <div className={styles.expandedText}>
                                <strong>Usuario GDE:</strong>{" "}
                                {funcionario.usuario_gde}{" "}
                                <button
                                  title={`Copiar al portapapeles`}
                                  type="button"
                                  onClick={() => {
                                    navigator.clipboard.writeText(
                                      funcionario.usuario_gde
                                    );
                                  }}
                                  className={styles.copyButton}
                                  aria-label="Copiar"
                                >
                                  <FaRegCopy />
                                </button>
                                <a
                                  title={`Ir a GDE`}
                                  href={`https://portal.gde.gob.ar/`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <FaExternalLinkAlt />
                                </a>
                              </div>
                              <div className={styles.expandedText}>
                                <strong>Reparticion GDE:</strong>{" "}
                                {funcionario.reparticion_gde.nombre}
                                <button
                                  title={`Copiar al portapapeles`}
                                  type="button"
                                  onClick={() => {
                                    navigator.clipboard.writeText(
                                      funcionario.reparticion_gde.nombre
                                    );
                                  }}
                                  className={styles.copyButton}
                                  aria-label="Copiar"
                                >
                                  <FaRegCopy />
                                </button>
                                <a
                                  title={`Ir a GDE`}
                                  href={`https://portal.gde.gob.ar/`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <FaExternalLinkAlt />
                                </a>
                              </div>
                              <div className={styles.expandedText}>
                                <strong>Sector GDE:</strong>{" "}
                                {funcionario.sector_gde.nombre}
                                <button
                                  title={`Copiar al portapapeles`}
                                  type="button"
                                  onClick={() => {
                                    navigator.clipboard.writeText(
                                      funcionario.sector_gde.nombre
                                    );
                                  }}
                                  className={styles.copyButton}
                                  aria-label="Copiar"
                                >
                                  <FaRegCopy />
                                </button>
                                <a
                                  title={`Ir a GDE`}
                                  href={`https://portal.gde.gob.ar/`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <FaExternalLinkAlt />
                                </a>
                              </div>
                              <div className={styles.expandedText}>
                                <strong>Revista:</strong> {funcionario.revista}
                              </div>
                            </div>
                          )}
                        {window.innerWidth >= 768 &&
                          window.innerWidth < 992 && (
                            <div>
                              <div className={styles.expandedText}>
                                <strong>Interno:</strong> {funcionario.interno}
                              </div>
                              <div className={styles.expandedText}>
                                <strong>Correo electrónico:</strong>{" "}
                                <a
                                  href={`https://outlook.office.com/owa/?path=/mail/action/compose&to=${encodeURIComponent(
                                    funcionario.correo
                                  )}`}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    const target =
                                      e.target as HTMLAnchorElement;
                                    const href = target.getAttribute("href");
                                    if (href) {
                                      window.open(
                                        href,
                                        "_blank",
                                        "toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=yes, resizable=yes, width=600, height=400"
                                      );
                                    }
                                  }}
                                >
                                  {funcionario.correo}
                                </a>
                              </div>
                              <div className={styles.expandedText}>
                                <strong>Usuario GDE:</strong>{" "}
                                {funcionario.usuario_gde}
                                <button
                                  title={`Copiar al portapapeles`}
                                  type="button"
                                  onClick={() => {
                                    navigator.clipboard.writeText(
                                      funcionario.usuario_gde
                                    );
                                  }}
                                  className={styles.copyButton}
                                  aria-label="Copiar"
                                >
                                  <FaRegCopy />
                                </button>
                                <a
                                  title={`Ir a GDE`}
                                  href={`https://portal.gde.gob.ar/`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <FaExternalLinkAlt />
                                </a>
                              </div>
                              <div className={styles.expandedText}>
                                <strong>Reparticion GDE:</strong>{" "}
                                {funcionario.reparticion_gde.nombre}
                                <button
                                  title={`Copiar al portapapeles`}
                                  type="button"
                                  onClick={() => {
                                    navigator.clipboard.writeText(
                                      funcionario.reparticion_gde.nombre
                                    );
                                  }}
                                  className={styles.copyButton}
                                  aria-label="Copiar"
                                >
                                  <FaRegCopy />
                                </button>
                                <a
                                  title={`Ir a GDE`}
                                  href={`https://portal.gde.gob.ar/`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <FaExternalLinkAlt />
                                </a>
                              </div>
                              <div className={styles.expandedText}>
                                <strong>Sector GDE:</strong>{" "}
                                {funcionario.sector_gde.nombre}
                                <button
                                  title={`Copiar al portapapeles`}
                                  type="button"
                                  onClick={() => {
                                    navigator.clipboard.writeText(
                                      funcionario.sector_gde.nombre
                                    );
                                  }}
                                  className={styles.copyButton}
                                  aria-label="Copiar"
                                >
                                  <FaRegCopy />
                                </button>
                                <a
                                  title={`Ir a GDE`}
                                  href={`https://portal.gde.gob.ar/`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <FaExternalLinkAlt />
                                </a>
                              </div>
                              <div className={styles.expandedText}>
                                <strong>Revista:</strong> {funcionario.revista}
                              </div>
                            </div>
                          )}
                        {window.innerWidth >= 992 &&
                          window.innerWidth < 1200 && (
                            <div>
                              <div className={styles.expandedText}>
                                <strong>Reparticion GDE:</strong>{" "}
                                {funcionario.reparticion_gde.nombre}
                                <button
                                  title={`Copiar al portapapeles`}
                                  type="button"
                                  onClick={() => {
                                    navigator.clipboard.writeText(
                                      funcionario.reparticion_gde.nombre
                                    );
                                  }}
                                  className={styles.copyButton}
                                  aria-label="Copiar"
                                >
                                  <FaRegCopy />
                                </button>
                                <a
                                  title={`Ir a GDE`}
                                  href={`https://portal.gde.gob.ar/`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <FaExternalLinkAlt />
                                </a>
                              </div>
                              <div className={styles.expandedText}>
                                <strong>Sector GDE:</strong>{" "}
                                {funcionario.sector_gde.nombre}
                                <button
                                  title={`Copiar al portapapeles`}
                                  type="button"
                                  onClick={() => {
                                    navigator.clipboard.writeText(
                                      funcionario.sector_gde.nombre
                                    );
                                  }}
                                  className={styles.copyButton}
                                  aria-label="Copiar"
                                >
                                  <FaRegCopy />
                                </button>
                                <a
                                  title={`Ir a GDE`}
                                  href={`https://portal.gde.gob.ar/`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <FaExternalLinkAlt />
                                </a>
                              </div>
                              <div className={styles.expandedText}>
                                <strong>Revista:</strong> {funcionario.revista}
                              </div>
                            </div>
                          )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {renderTableBody()}
            </tbody>
          </Table>
          {/* Toast para mostrar notificaciones */}
          <Toast
            onClose={() => setShowToast(false)}
            show={showToast}
            delay={3000}
            autohide
            className={`${styles.toastCenter} bg-success text-white`}
          >
            <Toast.Body>{toastMessage}</Toast.Body>
          </Toast>
        </div>
        {paginationControls}
      </div>
      <Footer />
    </>
  );
};

export default Grilla;
